/**
 * FreeShippingProgress — cart drawer free-shipping goal with live progress.
 *
 * - Subscribes to the theme's `cart:change` event (same contract as `free-shipping-bar`).
 * - Recomputes shipping-eligible subtotal from `event.detail.cart` (requires_shipping lines,
 *   minus cart-level discounts) so updates stay in sync with the mini-cart (qty, remove, add).
 *
 * Placeholders in message HTML (set via Liquid data attributes):
 * - Recommend [[amount]] / [[goal]] in theme settings (Shopify disallows "{{...}}" in section defaults).
 * - {{amount}} or [[amount]] — remaining amount (formatted money) before the goal
 * - {{goal}} or [[goal]] — goal threshold (formatted money), useful in the "reached" message
 */

if (!customElements.get('free-shipping-progress')) {
  class FreeShippingProgress extends HTMLElement {
    static get observedAttributes() {
      return ['threshold', 'total-price'];
    }

    constructor() {
      super();

      this._onCartChanged = this._onCartChanged.bind(this);

      const raw = this.getAttribute('threshold') || '0';
      const dollars = parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
      const thresholdCents = Math.round(dollars * 100);
      /** Attribute is normalized to cents, consistent with Shopify cart line `*_price` fields */
      this.setAttribute('threshold', String(thresholdCents));

      this._formatter = new Intl.NumberFormat(Shopify.locale, {
        style: 'currency',
        currency: Shopify.currency.active
      });
    }

    connectedCallback() {
      document.addEventListener('cart:change', this._onCartChanged);
      this._ensureRefs();
      this._render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChanged);
    }

    get totalPriceCents() {
      return Number.parseInt(this.getAttribute('total-price') || '0', 10) || 0;
    }

    set totalPriceCents(value) {
      this.setAttribute('total-price', String(value));
    }

    attributeChangedCallback() {
      this._render();
    }

    /**
     * Mirrors theme `free-shipping-bar` logic: sum `final_line_price` for shippable lines,
     * then subtract cart-level discount allocations.
     * @param {Record<string, unknown>} cart Shopify cart object from `/cart/*.js` or Section API
     */
    _shippingSubtotalFromCart(cart) {
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const apps = Array.isArray(cart?.cart_level_discount_applications)
        ? cart.cart_level_discount_applications
        : [];

      const shippingLines = items.filter((item) => item.requires_shipping);
      const lineSum = shippingLines.reduce((sum, item) => sum + (item.final_line_price || 0), 0);
      const cartDiscount = apps.reduce((sum, d) => sum + (d.total_allocated_amount || 0), 0);
      return Math.max(0, lineSum - cartDiscount);
    }

    /** @param {CustomEvent<{ cart?: Record<string, unknown> }>} event */
    _onCartChanged(event) {
      const cart = event.detail?.cart;
      if (!cart) return;
      this.totalPriceCents = this._shippingSubtotalFromCart(cart);
    }

    _ensureRefs() {
      this._messageEl = this.querySelector('[data-fsp-message]');
      this._fillEl = this.querySelector('[data-fsp-fill]');
      this._thumbEl = this.querySelector('[data-fsp-thumb]');
      this._trackEl = this.querySelector('[data-fsp-track]');
    }

    _formatMoneyFromCents(cents) {
      return this._formatter.format(cents / 100);
    }

    /**
     * Escape text inserted into merchant-controlled HTML templates (amount/goal only).
     * @param {string} str
     */
    _escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    /** @param {string} template */
    _applyPlaceholders(template, { amount, goal }) {
      return template
        .replace(/\{\{\s*amount\s*\}\}|\[\[amount\]\]/gi, this._escapeHtml(amount))
        .replace(/\{\{\s*goal\s*\}\}|\[\[goal\]\]/gi, this._escapeHtml(goal));
    }

    _render() {
      if (!this._messageEl || !this._fillEl || !this._thumbEl || !this._trackEl) {
        this._ensureRefs();
      }

      const threshold = Number.parseInt(this.getAttribute('threshold') || '0', 10) || 0;
      const total = this.totalPriceCents;
      const goalFormatted = this._formatMoneyFromCents(threshold);

      let pct = 0;
      if (threshold > 0) {
        pct = Math.min(100, Math.max(0, (total / threshold) * 100));
      } else {
        pct = 100;
      }

      const beforeTpl = this.getAttribute('data-message-before') || '';
      const afterTpl = this.getAttribute('data-message-after') || '';
      const reached = threshold > 0 && total >= threshold;

      if (reached) {
        this._messageEl.innerHTML = this._applyPlaceholders(afterTpl, {
          amount: this._formatMoneyFromCents(0),
          goal: goalFormatted
        });
      } else {
        const remainingCents = Math.max(0, threshold - total);
        this._messageEl.innerHTML = this._applyPlaceholders(beforeTpl, {
          amount: this._formatMoneyFromCents(remainingCents),
          goal: goalFormatted
        });
      }

      this._fillEl.style.width = `${pct}%`;
      this._thumbEl.style.left = `${pct}%`;
      this.style.setProperty('--fsp-pct', `${pct}%`);

      this._trackEl.setAttribute('aria-valuemin', '0');
      this._trackEl.setAttribute('aria-valuemax', String(threshold));
      this._trackEl.setAttribute('aria-valuenow', String(Math.min(total, threshold)));
    }
  }

  window.customElements.define('free-shipping-progress', FreeShippingProgress);
}
